if (!customElements.get('custom-swiper')) {
  customElements.define(
    'custom-swiper',
    class CustomSwiper extends HTMLElement {
      constructor() {
        super();
        this.thumbsSwiper = null;
        this.mainSwiper = null;
        this._scrubbing = false;
        this._scrubPointerId = null;
        this._scrubStartX = 0;
        this._scrubStartTime = 0;
        this._scrubVideo = null;
        this._scrubMoved = false;
        this._scrubWidth = 1;
        this._pendingTime = null;
        this._isSeeking = false;
        this._rafId = 0;
        this._hovered360 = false;
        this.handleResize = this.handleResize.bind(this);
        this.on360PointerDown = this.on360PointerDown.bind(this);
        this.on360PointerMove = this.on360PointerMove.bind(this);
        this.on360PointerUp = this.on360PointerUp.bind(this);
        this.on360MouseOver = this.on360MouseOver.bind(this);
        this.on360MouseOut = this.on360MouseOut.bind(this);
        this.onVideoSeeked = this.onVideoSeeked.bind(this);
      }

      connectedCallback() {
        this.desktopMode = this.getAttribute("data-desktop") || "false";
        this.updateSwiperState();
        window.addEventListener("resize", this.handleResize);
        this.bind360Controls();
        this.prepare360Videos();
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this.handleResize);
        this.unbind360Controls();
        this.destroySwipers();
      }

      getMain360Slide(target) {
        const slide = target?.closest?.(".product-main-slide--360, .product-main-slide");
        if (!slide || !this.contains(slide)) return null;
        if (!slide.closest("[data-main-swiper]")) return null;
        if (!slide.querySelector("video[data-360-video]")) return null;
        return slide;
      }

      allows360Autoplay() {
        return this.getAttribute("data-360-autoplay") !== "false";
      }

      prepare360Videos() {
        // Remove leftover dual-buffer videos from previous implementation.
        this.querySelectorAll("video[data-360-buffer]").forEach((el) => el.remove());

        const keepPaused = !this.allows360Autoplay();

        this.querySelectorAll("[data-main-swiper] video[data-360-video]").forEach((video) => {
          video.preload = "auto";
          video.muted = true;
          video.playsInline = true;
          if (keepPaused) {
            video.autoplay = false;
            video.removeAttribute("autoplay");
            video.pause();
          }
          if (video.readyState < 2) video.load();
        });
      }

      bind360Controls() {
        if (this._360Bound) return;
        this._360Bound = true;
        this.addEventListener("pointerdown", this.on360PointerDown);
        this.addEventListener("pointermove", this.on360PointerMove);
        this.addEventListener("pointerup", this.on360PointerUp);
        this.addEventListener("pointercancel", this.on360PointerUp);
        this.addEventListener("lostpointercapture", this.on360PointerUp);
        this.addEventListener("mouseover", this.on360MouseOver);
        this.addEventListener("mouseout", this.on360MouseOut);
      }

      unbind360Controls() {
        if (!this._360Bound) return;
        this._360Bound = false;
        this.removeEventListener("pointerdown", this.on360PointerDown);
        this.removeEventListener("pointermove", this.on360PointerMove);
        this.removeEventListener("pointerup", this.on360PointerUp);
        this.removeEventListener("pointercancel", this.on360PointerUp);
        this.removeEventListener("lostpointercapture", this.on360PointerUp);
        this.removeEventListener("mouseover", this.on360MouseOver);
        this.removeEventListener("mouseout", this.on360MouseOut);
        this.end360Scrub();
      }

      pause360Slide(slide) {
        slide?.querySelector("video[data-360-video]")?.pause();
      }

      play360Slide(slide) {
        if (!this.allows360Autoplay() || !slide || this._scrubbing) return;
        const video = slide.querySelector("video[data-360-video]");
        video?.play().catch(() => {});
      }

      sync360Playback() {
        const active = this.mainSwiper?.slides?.[this.mainSwiper.activeIndex];

        this.querySelectorAll("[data-main-swiper] video[data-360-video]").forEach((video) => {
          const slide = video.closest(".product-main-slide");
          const isActive = slide === active;

          if (!this.allows360Autoplay() || !isActive || this._scrubbing || this._hovered360) {
            video.pause();
            return;
          }

          video.play().catch(() => {});
        });
      }

      on360MouseOver(event) {
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

        const slide = this.getMain360Slide(event.target);
        if (!slide) return;
        if (slide.contains(event.relatedTarget)) return;

        this._hovered360 = true;
        slide.classList.add("is-360-hovered");
        this.pause360Slide(slide);
      }

      on360MouseOut(event) {
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

        const slide = this.getMain360Slide(event.target);
        if (!slide) return;
        if (slide.contains(event.relatedTarget)) return;

        this._hovered360 = false;
        slide.classList.remove("is-360-hovered");

        if (!this._scrubbing) {
          this.play360Slide(slide);
        }
      }

      normalizeTime(time, duration) {
        if (!duration || Number.isNaN(duration)) return 0;
        const frame = 1 / 30;
        const snapped = Math.round(time / frame) * frame;
        return ((snapped % duration) + duration) % duration;
      }

      queueSeek(time) {
        this._pendingTime = time;
        if (this._isSeeking) return;
        this.flushSeek();
      }

      flushSeek() {
        const video = this._scrubVideo;
        if (this._pendingTime == null || !video) {
          this._isSeeking = false;
          return;
        }

        const time = this._pendingTime;
        this._pendingTime = null;
        this._isSeeking = true;

        try {
          if (Math.abs((video.currentTime || 0) - time) < 0.001) {
            this._isSeeking = false;
            if (this._pendingTime != null) this.flushSeek();
            return;
          }

          video.addEventListener("seeked", this.onVideoSeeked, { once: true });

          if (typeof video.fastSeek === "function") {
            video.fastSeek(time);
          } else {
            video.currentTime = time;
          }
        } catch (e) {
          this._isSeeking = false;
        }
      }

      onVideoSeeked() {
        if (this._pendingTime != null) {
          this.flushSeek();
        } else {
          this._isSeeking = false;
        }
      }

      on360PointerDown(event) {
        if (event.button != null && event.button !== 0) return;

        const slide = this.getMain360Slide(event.target);
        if (!slide) return;

        const video = slide.querySelector("video[data-360-video]");
        if (!video) return;
        if (event.target.closest(".pdp-vertical-badges, .pdp-horizontal-badges, a, button")) return;

        video.pause();

        this._scrubbing = true;
        this._scrubMoved = false;
        this._scrubPointerId = event.pointerId;
        this._scrubStartX = event.clientX;
        this._scrubVideo = video;
        this._scrubStartTime = video.currentTime || 0;
        this._scrubWidth = Math.max(slide.offsetWidth || video.clientWidth || 1, 1);
        this._pendingTime = null;
        this._isSeeking = false;

        try {
          this.setPointerCapture(event.pointerId);
        } catch (e) {
          // ignore
        }

        if (this.mainSwiper) this.mainSwiper.allowTouchMove = false;
        slide.classList.add("is-360-scrubbing");
        document.documentElement.classList.add("is-360-scrubbing");
      }

      on360PointerMove(event) {
        if (!this._scrubbing || event.pointerId !== this._scrubPointerId) return;

        const video = this._scrubVideo;
        const duration = video?.duration;
        if (!video || !duration || Number.isNaN(duration)) return;

        const dx = event.clientX - this._scrubStartX;
        if (Math.abs(dx) > 3) {
          this._scrubMoved = true;
          if (event.cancelable) event.preventDefault();
        }

        let nextTime = this._scrubStartTime - (dx / this._scrubWidth) * duration;
        nextTime = this.normalizeTime(nextTime, duration);

        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
          this._rafId = 0;
          this.queueSeek(nextTime);
        });
      }

      on360PointerUp(event) {
        if (!this._scrubbing) return;
        if (this._scrubPointerId != null && event.pointerId !== this._scrubPointerId) return;
        this.end360Scrub();
      }

      end360Scrub() {
        if (!this._scrubbing) return;

        if (this._rafId) {
          cancelAnimationFrame(this._rafId);
          this._rafId = 0;
        }

        const video = this._scrubVideo;
        const slide = video?.closest(".product-main-slide");
        const finalTime = this._pendingTime;

        if (video && finalTime != null) {
          try {
            video.currentTime = finalTime;
          } catch (e) {
            // ignore
          }
        }

        this._scrubbing = false;
        this._scrubPointerId = null;
        this._scrubVideo = null;
        this._pendingTime = null;
        this._isSeeking = false;
        this._scrubMoved = false;

        slide?.classList.remove("is-360-scrubbing");
        document.documentElement.classList.remove("is-360-scrubbing");
        if (this.mainSwiper) this.mainSwiper.allowTouchMove = true;

        if (!video) return;

        video.pause();

        if (this.allows360Autoplay() && !this._hovered360) {
          video.play().catch(() => {});
        }
      }

      handleResize() {
        this.updateSwiperState();
      }

      updateSwiperState() {
        const isDesktop = window.matchMedia("(min-width: 960px)").matches;
        const mode = this.desktopMode;
        const shouldInit = mode === "both" || (mode === "true" && isDesktop) || (mode !== "true" && mode !== "both" && !isDesktop);

        if (shouldInit) {
          if (!this.mainSwiper) this.initSwipers();
        } else {
          this.destroySwipers();
        }
      }

      initSwipers() {
        const thumbsEl = this.querySelector("[data-thumbnails-swiper]");
        thumbsEl.classList.remove('hidden');
        const mainEl = this.querySelector("[data-main-swiper]");
        if (!thumbsEl || !mainEl) return;

        thumbsEl.classList.add("swiper");
        mainEl.classList.add("swiper");

        thumbsEl.querySelector('.wrapper').classList.add('swiper-wrapper');
        mainEl.querySelector('.wrapper').classList.add('swiper-wrapper');

        thumbsEl.querySelectorAll(".product-thumbs-slide").forEach(el => el.classList.add("swiper-slide"));
        mainEl.querySelectorAll(".product-main-slide").forEach(el => el.classList.add("swiper-slide"));

        const isThumbDesktop = window.matchMedia("(min-width: 769px)").matches;
        const desktopGap = Number(this.getAttribute("data-thumb-gap")) || 8;
        const mobileGap = Number(this.getAttribute("data-thumb-gap-mobile"));
        const thumbGap = !isThumbDesktop && mobileGap ? mobileGap : desktopGap;

        this.thumbsSwiper = new Swiper(thumbsEl, {
          spaceBetween: thumbGap,
          slidesPerView: "auto",
          watchSlidesProgress: true,
        });

        this.mainSwiper = new Swiper(mainEl, {
          loop: this.getAttribute("data-loop") === "true",
          spaceBetween: 8,
          slidesPerView: 1,
          navigation: {
            nextEl: this.querySelector(".product-media__button-next"),
            prevEl: this.querySelector(".product-media__button-prev"),
          },
          thumbs: {
            swiper: this.thumbsSwiper,
          },
        });

        this.mainSwiper.on("slideChange", () => this.sync360Playback());
        this.prepare360Videos();
        this.sync360Playback();
      }

      destroySwipers() {
        if (this.mainSwiper) {
          this.mainSwiper.destroy(true, true);
          this.mainSwiper = null;
        }
        if (this.thumbsSwiper) {
          this.thumbsSwiper.destroy(true, true);
          this.thumbsSwiper = null;
        }

        const thumbsEl = this.querySelector("[data-thumbnails-swiper]");
        thumbsEl.classList.add('hidden');
        const mainEl = this.querySelector("[data-main-swiper]");

        if (thumbsEl) {
          thumbsEl.querySelector('.wrapper').classList.remove('swiper-wrapper');
          thumbsEl.classList.remove("swiper");
          thumbsEl.querySelectorAll(".product-thumbs-slide").forEach(el => el.classList.remove("swiper-slide"));
        }

        if (mainEl) {
          mainEl.querySelector('.wrapper').classList.remove('swiper-wrapper');
          mainEl.classList.remove("swiper");
          mainEl.querySelectorAll(".product-main-slide").forEach(el => el.classList.remove("swiper-slide"));
        }
      }

      refresh() {
        this.destroySwipers();
        this.updateSwiperState();
      }
    }
  );
}
